import type { Extensions } from "@tiptap/core";
import { useValue } from "@tldraw/state-react";
import type { TLRichText, TLShapeId } from "@tldraw/tlschema";
import { richTextPlainText } from "../../shared/rich-text-plain";
import type { PlanroomEditor } from "./editor";
import { useEditor } from "./hooks";
import { RichTextLabel as EngineRichTextLabel, richTextHtml } from "./engine/builtin/rich-label";

// Rich text for shapes outside the engine's built-ins (documents and tables):
// rendering with the board's text extensions, and an editable label.

/** The board's rich-text extensions: those of its text shape. */
function boardExtensions(editor: PlanroomEditor): Extensions | undefined {
  const util = (editor as unknown as { getShapeUtil(type: string): { extensions?: Extensions } }).getShapeUtil("text");
  return util?.extensions;
}

export function renderHtmlFromRichText(editor: PlanroomEditor, richText: TLRichText) {
  return richTextHtml(richText, boardExtensions(editor));
}

export function renderPlaintextFromRichText(_editor: PlanroomEditor, richText: TLRichText) {
  return richTextPlainText(richText);
}

export type RichTextLabelProps = {
  shapeId: TLShapeId | string;
  type: string;
  richText: TLRichText;
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: number;
  textAlign?: "start" | "center" | "end";
  verticalAlign?: "start" | "middle" | "end";
  labelColor?: string;
  isSelected?: boolean;
  textWidth?: number;
  textHeight?: number;
  wrap?: boolean;
  showTextOutline?: boolean;
};

/** A shape's rich-text label, edited in place while its shape is being edited. */
export function RichTextLabel(props: RichTextLabelProps) {
  const editor = useEditor();
  const editing = useValue("label editing", () => editor.getEditingShapeId() === props.shapeId, [
    editor,
    props.shapeId,
  ]);
  return (
    <EngineRichTextLabel
      editor={editor}
      shapeId={props.shapeId as TLShapeId}
      type={props.type}
      richText={props.richText}
      editing={editing}
      extensions={boardExtensions(editor)}
      style={{
        fontFamily: props.fontFamily,
        fontSize: props.fontSize,
        lineHeight: props.lineHeight,
        color: props.labelColor,
        textAlign: props.textAlign === "center" ? "center" : props.textAlign === "end" ? "right" : "left",
        width: props.textWidth,
        minHeight: props.textHeight,
        whiteSpace: props.wrap ? "pre-wrap" : "pre",
        overflowWrap: props.wrap ? "break-word" : undefined,
      }}
    />
  );
}
